import React, { type ComponentProps } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ScrollView, StyleSheet } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { BodyText, Button, DisplayText, IllusDelivery_1, Image, theme } from '@sg/garnish';

import { BUNDLE_TRANSITION_DURATION } from '../../Bundle.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleNoLocation = (props: Props) => {
  const { title, description, ctaLabel, onSelectLocation } = props;

  const { formatMessage } = useIntl();

  return (
    <Animated.View style={styles.container} entering={FADE_IN} exiting={FADE_OUT}>
      <ScrollView contentContainerStyle={styles.contentContainer}>
        <Image
          alt={formatMessage(messages.illustrationA11y)}
          source={IllusDelivery_1}
          contentFit="contain"
          style={styles.illustration}
        />

        <DisplayText accessibilityRole="header" sizeMatch={TITLE_SIZE_MATCH} style={styles.title}>
          {title}
        </DisplayText>

        <BodyText sizeMatch={DESCRIPTION_SIZE_MATCH} style={styles.description}>
          {description}
        </BodyText>

        <Button
          palette="primary"
          size="large"
          style={styles.selectLocationButton}
          onPress={onSelectLocation}
        >
          {ctaLabel}
        </Button>
      </ScrollView>
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const FADE_IN_DELAY = 500;

const FADE_IN = FadeIn.duration(BUNDLE_TRANSITION_DURATION).delay(FADE_IN_DELAY);
const FADE_OUT = FadeOut.duration(BUNDLE_TRANSITION_DURATION);

const ILLUSTRATION_SIZE = 200;
const SELECT_LOCATION_BUTTON_MIN_WIDTH = 193;

const TITLE_SIZE_MATCH: ComponentProps<typeof DisplayText>['sizeMatch'] = ['24'];
const DESCRIPTION_SIZE_MATCH: ComponentProps<typeof BodyText>['sizeMatch'] = ['16'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing['10'],
    paddingHorizontal: theme.spacing['6'],
  },
  illustration: {
    width: ILLUSTRATION_SIZE,
    height: ILLUSTRATION_SIZE,
  },
  title: {
    marginVertical: theme.spacing['4'],
    textAlign: 'center',
  },
  description: {
    marginBottom: theme.spacing['10'],
    textAlign: 'center',
  },
  selectLocationButton: {
    minWidth: SELECT_LOCATION_BUTTON_MIN_WIDTH,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  illustrationA11y: {
    defaultMessage: 'Delivery courier riding a scooter',
    description: 'Bundle | No location | Illustration accessibility label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  title: string;
  description: string;
  ctaLabel: string;
  onSelectLocation: () => void;
};
